import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Loader2, KeyRound } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  return String(detail);
}

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    if (password !== confirm) {
      setError("Passwords do not match.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, new_password: password }, { withCredentials: true });
      setDone(true);
      setTimeout(() => navigate("/login"), 2000);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full bg-white border border-slate-300 rounded-sm px-4 py-3 text-base text-[#0F172A] placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#FF5A00] focus:border-[#FF5A00] transition-shadow duration-200";

  return (
    <div className="min-h-screen bg-[#0F172A] noise-overlay flex items-center justify-center px-4" data-testid="reset-password-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <img src="/assets/aegis-logo-dark.webp" alt="Aegis Subsurface LLC logo" className="h-24 w-auto inline-block" />
          <h1 className="mt-6 font-heading text-2xl font-black uppercase tracking-tight text-white" data-testid="reset-heading">
            Set New Password
          </h1>
        </div>

        <div className="bg-white rounded-sm p-8 border border-slate-200">
          {done ? (
            <div className="text-center" data-testid="reset-success">
              <KeyRound className="w-10 h-10 text-[#FF5A00] mx-auto" />
              <p className="mt-4 text-base text-slate-700">Password updated. Redirecting to login...</p>
            </div>
          ) : !token ? (
            <p className="text-sm text-red-700" data-testid="reset-no-token">
              This reset link is missing its token. Please request a new one from the{" "}
              <Link to="/forgot-password" className="text-[#FF5A00] font-semibold">forgot password</Link> page.
            </p>
          ) : (
            <form onSubmit={submit} data-testid="reset-form">
              {error && (
                <div className="mb-5 bg-red-50 border-l-4 border-red-600 px-4 py-3 text-sm text-red-700" data-testid="reset-error">
                  {error}
                </div>
              )}
              <label htmlFor="reset-password" className="block text-xs font-bold uppercase tracking-wider text-[#0F172A] mb-2">
                New Password
              </label>
              <input id="reset-password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className={inputCls} placeholder="At least 8 characters" data-testid="reset-password-input" />
              <label htmlFor="reset-confirm" className="block text-xs font-bold uppercase tracking-wider text-[#0F172A] mb-2 mt-5">
                Confirm Password
              </label>
              <input id="reset-confirm" type="password" required value={confirm} onChange={(e) => setConfirm(e.target.value)} className={inputCls} placeholder="Repeat password" data-testid="reset-confirm-input" />
              <button
                type="submit"
                disabled={loading}
                data-testid="reset-submit-button"
                className="mt-7 w-full inline-flex items-center justify-center gap-2 bg-[#FF5A00] hover:bg-[#E04F00] disabled:opacity-60 text-white font-heading font-bold uppercase tracking-wider px-8 py-3.5 rounded-sm transition-all duration-200 hover:-translate-y-0.5 hover:shadow-[4px_4px_0px_#0F172A]"
              >
                {loading && <Loader2 className="w-5 h-5 animate-spin" />}
                {loading ? "Updating..." : "Update Password"}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
